import {inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';

/** TrustArc's consent API, injected on `window` by an external TrustArc script. */
interface TrustArcWindow extends Window {
  truste?: {
    eu?: {
      clickListener?: (arg: number) => void;
    };
  };
}

type PopoverElement = HTMLElement & {showPopover: () => void; hidePopover: () => void};

// TrustArc's two top-level fixed containers: the backdrop and the dialog wrapper.
// Promote these (not the nested .truste_popframe, which would lose its centering).
// Overlay first so top-layer LIFO stacks the wrapper above its backdrop.
const TRUSTARC_SELECTORS = ['.truste_overlay', '.truste_cm_outerdiv'] as const;
const PROMOTE_TIMEOUT_MS = 3000;

/**
 * Single entry point for opening the TrustArc cookie-preferences dialog.
 *
 * Wired from {@link AppComponent} (mounted in both the host document and the
 * configurator iframe) rather than only the footer, which renders `@if (!isModal)`
 * and is therefore absent inside the iframe.
 *
 * When the configurator `<udex-modal>` (a top-layer element) is open, TrustArc's
 * overlay would render under it despite its high z-index. {@link promoteToTopLayer}
 * lifts the TrustArc containers into the top layer so they stack above the modal.
 */
@Injectable({
  providedIn: 'root',
})
export class DcpCookiePreferencesService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);

  /** True when the Popover API is available. Feature-detected once at construction time. */
  private readonly supportsPopover = typeof HTMLElement.prototype.showPopover === 'function';

  private activeObserver: MutationObserver | null = null;
  private activeTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnDestroy(): void {
    this.teardownPromotion();
  }

  /** Opens the dialog. No-op on the server or when TrustArc has not loaded. */
  open(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    (window as TrustArcWindow).truste?.eu?.clickListener?.(1);
    try {
      this.promoteToTopLayer();
    } catch {
      // Promotion failure must never break open()
    }
  }

  private promoteToTopLayer(): void {
    if (!this.supportsPopover) {
      return;
    }

    this.teardownPromotion();

    // Immediate pass covers re-opens where TrustArc reuses existing DOM.
    if (this.tryPromoteAll()) {
      return;
    }

    // TrustArc injects asynchronously (~1–2 s after clickListener); watch for it.
    const observer = new MutationObserver(() => {
      try {
        if (this.tryPromoteAll()) {
          this.teardownPromotion();
        }
      } catch {
        this.teardownPromotion();
      }
    });

    const timer = setTimeout(() => {
      this.teardownPromotion();
    }, PROMOTE_TIMEOUT_MS);

    this.activeObserver = observer;
    this.activeTimer = timer;

    observer.observe(document.body, {childList: true, subtree: true});
  }

  /** Returns true when every selector target is present in the DOM. */
  private tryPromoteAll(): boolean {
    let allFound = true;
    for (const selector of TRUSTARC_SELECTORS) {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        this.promoteElement(el);
      } else {
        allFound = false;
      }
    }
    return allFound;
  }

  private promoteElement(el: HTMLElement): void {
    if (!this.supportsPopover) {
      return;
    }
    try {
      if (!el.matches(':popover-open')) {
        el.setAttribute('popover', 'manual');
        (el as PopoverElement).showPopover();
      }
    } catch {
      // Element may have been removed between query and promote.
    }
  }

  private teardownPromotion(): void {
    if (this.activeTimer !== null) {
      clearTimeout(this.activeTimer);
      this.activeTimer = null;
    }
    if (this.activeObserver !== null) {
      this.activeObserver.disconnect();
      this.activeObserver = null;
    }
  }
}
